import { activateOnKey } from '../../lib/activateOnKey';

type DonutSegment = { value: number; color: string; label?: string };

type Props = {
  data: DonutSegment[];
  size?: number;
  thickness?: number;
  centerLabel?: string;
  centerValue?: string;
  onSegmentClick?: (index: number) => void;
};

export function DonutChart({
  data,
  size = 180,
  thickness = 22,
  centerLabel = 'LOGS · 24H',
  centerValue,
  onSegmentClick,
}: Props) {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  const r = size / 2 - thickness / 2 - 2;
  const c = size / 2;
  let acc = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <title>{centerLabel}</title>
      <circle
        cx={c}
        cy={c}
        r={r}
        fill="none"
        stroke="rgba(255,255,255,0.04)"
        strokeWidth={thickness}
      />
      {data.map((d, i) => {
        const frac = d.value / total;
        const len = 2 * Math.PI * r;
        const dash = frac * len;
        const offset = -acc * len;
        acc += frac;
        return (
          <circle
            key={`${d.label ?? i}`}
            cx={c}
            cy={c}
            r={r}
            fill="none"
            stroke={d.color}
            strokeWidth={thickness}
            strokeDasharray={`${dash} ${len - dash}`}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${c} ${c})`}
            onClick={onSegmentClick ? () => onSegmentClick(i) : undefined}
            onKeyDown={
              onSegmentClick ? (e) => activateOnKey(e, () => onSegmentClick(i)) : undefined
            }
            // A clickable segment is a control, so it joins the tab order and
            // announces itself; a decorative one stays out of both.
            tabIndex={onSegmentClick ? 0 : undefined}
            role={onSegmentClick ? 'button' : undefined}
            aria-label={onSegmentClick ? d.label : undefined}
            style={onSegmentClick ? { cursor: 'pointer' } : undefined}
          >
            {d.label && <title>{`${d.label} · ${d.value.toLocaleString()}`}</title>}
          </circle>
        );
      })}
      <text
        x={c}
        y={c - 2}
        textAnchor="middle"
        fill="var(--fg-1)"
        fontSize="26"
        fontWeight="400"
        letterSpacing="-0.02em"
      >
        {centerValue ?? data.reduce((s, d) => s + d.value, 0)}
      </text>
      <text
        x={c}
        y={c + 16}
        textAnchor="middle"
        fill="var(--fg-4)"
        fontSize="9.5"
        letterSpacing="0.18em"
      >
        {centerLabel}
      </text>
    </svg>
  );
}
